'use client';

import React from 'react';
import Link from 'next/link';
import R2Image from '@/components/ui/R2Image';

export interface LogoProps {
  /** Logo image source path (resolved via R2Image/manifest or static path) */
  src: string;
  /** Accessible alternative text for the logo */
  alt: string;
  /** Destination URL for the logo link */
  href?: string;
  /** Optional CSS class name */
  className?: string;
  /** Image width in pixels */
  width?: number;
  /** Image height in pixels */
  height?: number;
  /** Whether the logo acts as an inline link (no wrapping link element) */
  inline?: boolean;
  /** Custom quality for the rendered image */
  quality?: number;
}

/**
 * Logo component — reusable header/footer logo.
 *
 * Uses R2Image for manifest-aware resolution and consistent srcSet behavior.
 * Passes through extra props to preserve compatibility with existing call sites.
 */
export const Logo: React.FC<LogoProps> = ({
  src,
  alt,
  href,
  className,
  width,
  height,
  inline = false,
  quality,
  ...rest
}) => {
  const isExternal =
    typeof href === 'string' &&
    (href.startsWith('http://') || href.startsWith('https://'));

  const CommonImage = (
    <R2Image
      path={src}
      alt={alt}
      width={width}
      height={height}
      quality={quality}
      className={className}
      unoptimized
      {...rest}
    />
  );

  if (inline) {
    return CommonImage;
  }

  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {CommonImage}
      </a>
    );
  }

  return <Link href={href ?? '/'}>{CommonImage}</Link>;
};

export default Logo;
